// warehouse_view.js — full-screen estate view.
//
// Two questions this screen answers, in this order:
//   1. What can move branch to branch?      (someone else needs it)
//   2. What can come back to the warehouse? (nobody needs it)
//
// Those two panels are exhaustive over trustworthy spare stock - every certain
// spare pack is in exactly one of them - and everything below them is the
// evidence for the two numbers at the top.
//
// All arithmetic lives in estate_model.js, which is pure and tested. This file
// fetches, arranges and explains.

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
import {
  buildRows, buildPendingMap, matchEstate, normaliseDrug,
  chooseUsageWindow, assessBranchesByWindow, monthsLabel,
} from "./estate_model";
import UsageTools from "./usage_tools";
import BranchStockTabs from "./BranchStockTabs";
import EstateTopLines from "./EstateTopLines";

const API = "https://api.pharmacegroup.co.uk";

const money = (n) =>
  `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The warehouse is a destination, not a branch: it has no pharmacyId, and the
// name is what appears on the transfer record and in the emails.
const WAREHOUSE_ID = null;
const WAREHOUSE_NAME = "Warehouse";

const CONF = {
  high:   { label: "steady",   colour: "#639922" },
  medium: { label: "fair",     colour: "#BA7517" },
  low:    { label: "unsure",   colour: "#888780" },
  none:   { label: "no usage", colour: "#888780" },
};

/* ===========================================================================
   Floating help. Hover or tap the ?; the bubble is absolutely positioned so it
   never disturbs the layout it is explaining.
   =========================================================================== */
function Help({ text, wide }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex", marginLeft: 6 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{
          width: 17, height: 17, borderRadius: "50%", cursor: "help",
          border: "1.5px solid #2b7de9", color: "#2b7de9", background: "#fff",
          fontSize: 11, fontWeight: 700, lineHeight: "14px", textAlign: "center",
          display: "inline-block", flex: "0 0 auto",
        }}
      >?</span>
      {open && (
        <span
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "calc(100% + 8px)", left: -6, zIndex: 1400,
            width: wide ? 380 : 300, background: "#fff", color: "#1f2937",
            border: "1px solid #2b7de9", borderRadius: 8, cursor: "default",
            boxShadow: "0 8px 24px rgba(15,23,42,0.2)", padding: "10px 12px",
            fontSize: "0.82em", lineHeight: 1.55, fontWeight: 400,
            whiteSpace: "normal", textAlign: "left",
          }}
        >{text}</span>
      )}
    </span>
  );
}

/* ===========================================================================
   Usage sparkline.

   The confidence word on a line is a verdict, and a verdict without its
   evidence invites exactly the question "why does that one say unsure and
   this one say steady?". Three bars answer it at a glance: a flat trio is
   steady, one tall bar beside two empty ones is not.

   A zero month is drawn as a visible baseline rather than nothing at all,
   because zero is an observation - the branch dispensed none that month -
   and drawing it as blank space would repeat the very mistake that made
   slow movers look untrustworthy in the first place.
   =========================================================================== */
// Deliberately one neutral colour. Amber and red already mean "spare" and
// "short" everywhere else on this screen, and spending them again on a
// confidence verdict made a merely uneven line look like an alert. The bars
// are here to show SHAPE - level, rising, one month standing alone - and the
// word beside them already carries the verdict.
function UsageBars({ monthly = [], packSize = 1 }) {
  const colour = "#64748b";
  const months = [...monthly].sort((a, b) => String(a.month).localeCompare(String(b.month)));
  if (!months.length) return <span style={{ fontSize: "0.75em", color: "#94a3b8" }}>no months</span>;

  const peak = Math.max(...months.map((m) => m.qty || 0), 1);
  const label = (ym) => {
    const t = String(ym || "");
    if (t.length !== 6) return t;
    return new Date(Number(t.slice(0, 4)), Number(t.slice(4, 6)) - 1)
      .toLocaleString("en-GB", { month: "short" });
  };

  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "flex-end", height: 22 }}>
      {months.map((m) => {
        const qty = m.qty || 0;
        const packs = packSize > 0 ? qty / packSize : qty;
        const h = qty > 0 ? Math.max(3, Math.round((qty / peak) * 20)) : 2;
        return (
          <span key={m.month}
            title={`${label(m.month)}: ${packs.toFixed(1)} pack${packs === 1 ? "" : "s"}`}
            style={{
              width: 7, height: h, borderRadius: 1,
              background: qty > 0 ? colour : "#cbd5e1",
              display: "inline-block",
            }} />
        );
      })}
    </span>
  );
}

/* ===========================================================================
   The guided tour.

   Highlights one region at a time and explains it. Auto-advances, but Next and
   Skip are always there - a timed overlay you cannot outrun is worse than no
   overlay, and people read at very different speeds.
   =========================================================================== */
const GUIDE = [
  {
    ref: "cover",
    title: "Start here: how long the shelf lasts",
    body: "Everything on this screen is worked out in months, not packs. Stock divided by how much a branch gets through in a month tells you how long it lasts. Set the number of months you want to keep, and anything beyond that is spare.",
  },
  {
    ref: "transfer",
    title: "Eligible for transfer",
    body: "Stock one branch has spare that another branch is actually short of. It is worked out product by product: the branch that is shortest gets served first. Click the panel to see every line, then use Raise all transfers to create the requests in one go.",
  },
  {
    ref: "warehouse",
    title: "Eligible for warehouse retrieval",
    body: "Spare stock that no branch needs. It is the leftover after every branch shortage has been covered, so it can only ever grow once the transfers above have been taken out. Click to see which branch is holding what.",
  },
  {
    ref: "unsure",
    title: "Held back on purpose",
    body: "Stock that looks spare, but only because of usage we do not trust - one month of data, or usage that swings wildly. It is never counted in the two panels above and is never moved automatically. Check these by hand.",
  },
  {
    ref: "idle",
    title: "Idle stock",
    body: "The widest figure on the screen: everything spare anywhere in the estate that has not been committed to a transfer. It takes in the branches excluded for out-of-date data and the lines held back on unreliable usage, so it is bigger than what you can act on today. The 'Can it move?' column on each line says which of those it is. The gap between this figure and the first two panels is the work worth doing — get the missing usage files in, and stock moves out of here and into the panels you can act on.",
  },
  {
    ref: "idle",
    title: "Idle stock — reading the working",
    body: "The list shows how each figure is arrived at rather than just the answer. On shelf is what was counted. Committed is what is already on a pending transfer, shown as a deduction. Spare is what is left after that and after the branch's own cover period. So a shelf of 8 with 2 already promised and 1 month's cover reads as 5 spare — and you can see why, instead of having to take it on trust.",
  },
  {
    ref: "branches",
    title: "Branch by branch — check the border first",
    body: "The coloured border tells you whether a branch's usage data can be trusted. Green means the last three months are in. Amber means it is one month behind, which is still used. Red means the data is missing or out of date, and that branch is left out of every figure on this screen. Inside the card, the counts are that branch's lines: short, about right, spare, or unsure.",
  },
  {
    ref: "detail",
    title: "Four tabs for one branch",
    body: "\u2018Excess/under stocked items\u2019 is stock this branch could spare, weighed against its own usage. \u2018In stock, no usage\u2019 and \u2018Usage, no stock\u2019 are the two edge cases usage figures alone cannot show. \u2018Top 200 lines\u2019 is this branch's biggest movers over the last three months, by value or by pack count.",
  },
  {
    ref: "detail",
    title: "Two sliders on the excess tab",
    body: "\u2018Number of months to leave at branch\u2019 re-runs the spare calculation at whatever cover you choose here, without touching the \u2018Keep enough stock for\u2019 slider above. \u2018Only show if transfer value greater than\u2019 hides anything not worth the trip. Every column header sorts \u2014 click again to reverse it.",
  },
  {
    ref: "detail",
    title: "Recipients, or the warehouse",
    body: "Each spare line lists every other branch actually short of it, and how many it needs. Where nothing in the estate is short, the line says \u2018Transfer to warehouse\u2019 instead \u2014 there is nowhere else for it to go.",
  },
  {
    ref: "detail",
    title: "Click a row for the packs behind it",
    body: "Clicking a row with stock opens the individual packs: how many, what size, batch and serial number where recorded, expiry, when it was first scanned, where it is, and what it is worth. Opened boxes are marked \u2018split\u2019 \u2014 they count as stock, but they are left out of working out what a pack is.",
  },
];

/* ===========================================================================
   Screen
   =========================================================================== */
export default function WarehouseView({ userData, onOpenDetail }) {
  const masked = useMaskNames();
  // The excess page reads the group from "userId" - the same value, stored
  // under an older name. Using "groupId" here silently fetched nothing.
  const groupId = localStorage.getItem("userId");

  const [monthsAhead, setMonthsAhead] = useState(3);
  const [monthsAverage] = useState(3);
  const [members, setMembers] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [usage, setUsage] = useState({ lookup: {}, meta: {}, monthly: {} });
  // pharmacyId -> { months: [ym...], current: bool } - which of the fetched
  // months this branch's own figures are actually averaged over.
  const [usageWindows, setUsageWindows] = useState({});
  // pharmacyId -> { status, why, window } - the coverage-aware replacement
  // for assessBranches(usage.meta, usage.monthly, ...). Computed once in
  // loadAll alongside usageWindows so the two can never disagree about which
  // months a branch is being judged on.
  const [branchFreshness, setBranchFreshness] = useState(new Map());
  const [transferData, setTransferData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [openPanel, setOpenPanel] = useState(null);   // transfer | warehouse | unsure
  const [selBranch, setSelBranch] = useState(null);
  const [selLine, setSelLine] = useState(null);   // `${pharmacyId}|${cleanName}` — expanded shelf line
  const [raising, setRaising] = useState(null);       // { done, total } while processing
  // The review grid, and which job it is doing. Branch-to-branch and warehouse
  // retrieval share every mechanism below - the split, the allocation caps, the
  // emails, /createAllocatedTransfer itself - and differ only in where the
  // stock is headed, so they are one dialog with a mode rather than two.
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [raiseMode, setRaiseMode] = useState("transfer"); // "transfer" | "warehouse"
  const [raiseDone, setRaiseDone] = useState({});    // groupKey -> reference | error text
  // Both default to ON: raising a transfer without telling the branch is the
  // surprising outcome, not the safe one, so switching an email off is a
  // deliberate act rather than something you forget to switch on.
  const [raiseOpts, setRaiseOpts] = useState({
    emailBranch: true, emailDriver: true, emailRecipients: true, driverEmail: "", note: "",
  });
  // `${groupKey}|${toPharmacyId}` -> packs. Seeded from the model's split, then
  // whatever the user types wins - the suggestion is a starting point, not a
  // decision, and the detail screen has always allowed this.
  const [raiseAlloc, setRaiseAlloc] = useState({});
  const [preview, setPreview] = useState(null);   // { title, html }
  const [showTransfers, setShowTransfers] = useState(false);
  // Which rows have their picked / received detail open. Keyed by row id, the
  // same way the detail screen does it, so the two behave identically.
  const [openPicked, setOpenPicked] = useState({});
  const [openReceived, setOpenReceived] = useState({});
  const [openLog, setOpenLog] = useState({});
  // The dialogue's own copy, fetched WITH completed rows.
  //
  // transferData feeds every figure on this screen and must stay pending-only:
  // widening it would make finished transfers read as stock still promised. But
  // a confirmed transfer is completed by definition, so a pending-only list
  // drops the row at the very moment the confirmation lands - the branch
  // confirms, and the evidence vanishes. Hence a separate fetch, used for
  // display only.
  const [transfersFull, setTransfersFull] = useState(null);
  const [transfersFullLoading, setTransfersFullLoading] = useState(false);
  const [lineSearch, setLineSearch] = useState("");
  const [lineSort, setLineSort] = useState("value");
  const [linePage, setLinePage] = useState(0);
  const LINES_PER_PAGE = 25;
  const [guideStep, setGuideStep] = useState(-1);
  const [guidePaused, setGuidePaused] = useState(false);
  const [showTop200, setShowTop200] = useState(false);

  const regions = {
    cover: useRef(null), transfer: useRef(null), warehouse: useRef(null),
    unsure: useRef(null), idle: useRef(null), branches: useRef(null), detail: useRef(null),
  };

  /* --- data ------------------------------------------------------------- */

  const token = () => localStorage.getItem("authToken");

  const loadAll = useCallback(async () => {
    setErr(null);
    try {
      const h = { Authorization: `Bearer ${token()}` };
      // One month more than we need, so a branch whose newest month is not
      // yet full has somewhere to fall back to without a second round trip.
      const usageMonthsBack = monthsAverage + 1;
      const [iRes, uRes, tRes, cRes] = await Promise.all([
        fetch(`${API}/ggetInventory?groupId=${groupId}`, { headers: h }),
        fetch(`${API}/getUsage3MonthsGroup?groupId=${groupId}&monthsBack=${usageMonthsBack}`, { headers: h }),
        fetch(`${API}/getTransfers?groupId=${groupId}`, { headers: h }),
        // Same endpoint the "Usage uploads" coverage table already uses to
        // flag a short or partial month. If it is unavailable for any reason
        // this whole screen still works - see graceOk below.
        fetch(`${API}/usageUploadCoverage`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...h },
          body: JSON.stringify({ groupId, months: 6 }),
        }).catch(() => null),
      ]);

      const iData = await iRes.json();
      const uData = await uRes.json();
      const tData = await tRes.json();
      let cData = null;
      if (cRes && cRes.ok) {
        try { cData = await cRes.json(); } catch { cData = null; }
      }

      // Same source the excess page uses - the group's member list is already
      // in local storage, so this screen must not invent a second way to get
      // it and risk the two disagreeing about who is in the estate.
      const membersList = JSON.parse(localStorage.getItem("members") || "[]");
      setMembers(membersList);
      setInventoryData(Array.isArray(iData) ? iData : []);
      setTransferData(Array.isArray(tData) ? tData : []);

      // ------------------------------------------------------------------
      // Which months are actually full, per branch.
      //
      // /getUsage3MonthsGroup zero-fills every item across the SAME calendar
      // window for every branch - that part is right and stays. What it
      // cannot tell us is whether the newest of those months has genuinely
      // been reported yet, or whether a branch simply has a handful of days
      // of it uploaded so far. That is what /usageUploadCoverage is for, so
      // it is asked here rather than guessed at again.
      //
      // If that call is unavailable, fall back to a plain calendar grace
      // period: the newest complete month is not trusted until a few days
      // into the following one have passed, which is enough time for most
      // branches to have filed it. Once real coverage data is in, this
      // fallback stops mattering - every branch is judged on its own actual
      // reporting instead of a single date for the whole estate.
      // ------------------------------------------------------------------
      const GRACE_DAYS = 5;
      const graceOk = new Date().getDate() > GRACE_DAYS;

      const typicalByMonth = {};
      const coverageByPharmacy = {};
      if (cData) {
        (cData.months || []).forEach((m) => { typicalByMonth[m.month] = m.typicalTradingDays; });
        (cData.pharmacies || []).forEach((p) => {
          const byMonth = {};
          (p.months || []).forEach((c) => { byMonth[c.month] = c; });
          coverageByPharmacy[p.pharmacyId] = byMonth;
        });
      }

      // The calendar months /getUsage3MonthsGroup actually returned, oldest
      // first - read off the data itself rather than recomputed by hand, so
      // this can never disagree with what the server sent.
      const monthsAsc = (Array.isArray(uData) && uData[0]?.monthly)
        ? uData[0].monthly.map((m) => m.month)
        : [];

      // Real dispensing anywhere in that window, independent of the coverage
      // call - a branch is never marked "no usage data" just because the
      // coverage endpoint happened to fail.
      const hasAnyUsage = new Set();
      (Array.isArray(uData) ? uData : []).forEach((item) => {
        if ((item.monthly || []).some((m) => (m.qty || 0) > 0)) hasAnyUsage.add(item.pharmacyId);
      });

      const windowByPharmacy = {};
      const pids = new Set([...membersList.map((m) => m.pharmacyId), ...hasAnyUsage]);
      for (const pid of pids) {
        windowByPharmacy[pid] = chooseUsageWindow({
          monthsAsc, coverageByMonth: coverageByPharmacy[pid] || {}, typicalByMonth,
          n: monthsAverage, graceOk,
        });
      }
      setUsageWindows(windowByPharmacy);

      setBranchFreshness(assessBranchesByWindow({
        monthsAsc, coverageByPharmacy, typicalByMonth, hasAnyUsage, members: membersList,
        n: monthsAverage, graceOk,
      }));

      // ---- usage lookup / monthly, trimmed to each branch's OWN window ---
      const lookup = {}, meta = {}, monthly = {};
      (Array.isArray(uData) ? uData : []).forEach((item) => {
        const parsed = normaliseDrug(item.drugName);
        const key = `${item.pharmacyId}_${parsed.key}`;
        const win = windowByPharmacy[item.pharmacyId]?.months
          || monthsAsc.slice(-monthsAverage);
        const winSet = new Set(win);
        const trimmed = (item.monthly || []).filter((m) => winSet.has(m.month));
        const sum = trimmed.reduce((t, m) => t + (m.qty || 0), 0);
        // Divided by the window itself, not by however many of those months
        // happen to have data - a branch reporting zero dispensing in a
        // genuinely full month is a real zero, not a gap to skip over.
        const avg = win.length ? sum / win.length : 0;

        lookup[key] = (lookup[key] || 0) + avg;
        if (!meta[key]) {
          meta[key] = {
            pharmacyId: item.pharmacyId, drugName: item.drugName, snomed: item.snomed,
            cleanName: parsed.key, packSize: parseFloat(item.packSize) || 0,
          };
        }
        if (!monthly[key]) monthly[key] = trimmed.map((m) => ({ month: m.month, qty: m.qty || 0 }));
        else trimmed.forEach((m, i) => { if (monthly[key][i]) monthly[key][i].qty += (m.qty || 0); });
      });
      setUsage({ lookup, meta, monthly });
    } catch (e) {
      setErr(e.message || "Could not load the estate");
    } finally {
      setLoading(false);
    }
  }, [groupId, monthsAverage]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* --- model ------------------------------------------------------------ */

  // Pending transfers are counted as though already actioned, on both sides.
  // That is what makes the panels settle after Raise all transfers instead of
  // re-offering the same stock and shipping it twice.
  const pending = useMemo(() => buildPendingMap(transferData), [transferData]);

  // Which branches can be reasoned about at all - now decided by
  // branchFreshness (coverage-aware: a genuinely full month, not just any
  // row for it), computed alongside usage itself in loadAll. Kept as
  // `freshness` below so buildRows/branchCards need no further changes.
  const freshness = branchFreshness;

  const rows = useMemo(() => buildRows({
    inventoryData,
    usageLookup: usage.lookup, usageMeta: usage.meta, usageMonthly: usage.monthly,
    members, monthsAhead, pending, freshness,
  }), [inventoryData, usage, members, monthsAhead, pending, freshness]);

  const model = useMemo(() => matchEstate(rows), [rows]);

  const branchCards = useMemo(() => {
    // Seeded from the full member list, not from the rows. A branch with no
    // stock and no usage is a fact worth showing - it means nobody has
    // uploaded anything for it - and a card that quietly fails to appear
    // looks identical to a branch that is simply doing fine.
    const blank = (pharmacyId, pharmacyName) => {
      const f = freshness.get(pharmacyId) || { status: "red", why: "no usage data at all" };
      return {
        pharmacyId, pharmacyName, status: f.status, why: f.why,
        short: 0, right: 0, spare: 0, unsure: 0, spareValue: 0, rows: [],
        outPacks: 0, outLines: 0, inPacks: 0, inLines: 0,
      };
    };

    const byBranch = new Map();
    for (const m of members) byBranch.set(m.pharmacyId, blank(m.pharmacyId, m.pharmacyName));

    for (const r of rows) {
      if (!byBranch.has(r.pharmacyId)) {
        // Stock for a branch the member list does not know about. Shown
        // rather than dropped, since silently discarding it would take its
        // stock out of every total on the screen.
        byBranch.set(r.pharmacyId, blank(r.pharmacyId, r.pharmacyName));
      }
      const b = byBranch.get(r.pharmacyId);
      b.rows.push(r);
      // Stock already promised away, and stock already on its way in. Both are
      // netted into every figure on this screen, so the counts explain why a
      // branch reads better or worse than its shelf suggests.
      const size = r.packSize > 0 ? r.packSize : 1;
      if (r.pendingOut > 0) { b.outPacks += r.pendingOut / size; b.outLines++; }
      if (r.pendingIn > 0) { b.inPacks += r.pendingIn / size; b.inLines++; }
      if (r.certainExcessPacks > 0) {
        b.spare++;
        b.spareValue += r.certainExcessPacks * r.packSize * r.unitValue;
      } else if (r.unsureExcessPacks > 0) b.unsure++;
      else if (r.deficitPacks > 0) b.short++;
      else b.right++;
    }

    // Branches with data first, most spare value at the top; the empty ones
    // gathered at the end in name order rather than scattered through.
    // Usable branches first, most spare value at the top; the ones nobody can
    // rely on gathered at the end.
    const rank = { green: 0, amber: 1, red: 2 };
    return [...byBranch.values()].sort((a, b) => {
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
      if (a.status === "red") return String(a.pharmacyName).localeCompare(String(b.pharmacyName));
      return b.spareValue - a.spareValue;
    });
  }, [rows, members, freshness]);

  /* Everything sitting spare that nobody has committed to move.
     
     Deliberately the WIDEST of the four figures, and a superset of the other
     three: it counts every branch, including the ones excluded for stale data,
     and every spare line, including those held back on unreliable usage. Stock
     already on a pending transfer is not idle and is excluded - the excess it
     is built on has already had that deducted.
     
     The three action panels answer "what can I do today". This one answers
     "how much is sitting there", which is the bigger and more uncomfortable
     number, and the one that goes stale quietly if nobody looks. */
  const idleStock = useMemo(() => {
    const lines = [];
    for (const r of rows) {
      if (!(r.excessPacks > 0)) continue;
      const value = r.excessPacks * r.packSize * r.unitValue;
      lines.push({
        row: r,
        packs: r.excessPacks,
        value,
        // Why it is or is not actionable, in the order that matters.
        status: !r.usable ? "branch excluded"
          : r.certainExcessPacks <= 0 ? "held back — usage unsure"
          : r.unsureExcessPacks > 0 ? `${r.certainExcessPacks} of ${r.excessPacks} pk can move`
          : "can move",
        actionable: r.usable && r.certainExcessPacks > 0,
      });
    }
    lines.sort((a, b) => b.value - a.value);
    return {
      lines,
      packs: lines.reduce((t, l) => t + l.packs, 0),
      value: lines.reduce((t, l) => t + l.value, 0),
      stuckValue: lines.filter((l) => !l.actionable).reduce((t, l) => t + l.value, 0),
    };
  }, [rows]);

  const pendingTransferCount = useMemo(
    () => (transferData || []).filter((t) => t.status === "pending" && !t.deleted).length,
    [transferData]
  );

  const usableCount = branchCards.filter((b) => b.status !== "red").length;
  const staleCount = branchCards.length - usableCount;

  /* --- raise transfers --------------------------------------------------- */

  /* --- raising transfers ------------------------------------------------
     One request per source branch and product, carrying every destination for
     it - which is the shape /createAllocatedTransfer expects, and the same
     call the detail screen's Transfer dialog makes. It returns a reference and
     sends the branch emails; the older /sendTransfer did neither, which is why
     nothing raised from here was reaching the transfers list.
     --------------------------------------------------------------------- */

  const raiseGroups = useMemo(() => {
    const bySource = new Map();
    for (const t of model.transfers) {
      const key = `${t.fromPharmacyId}|${t.cleanName}`;
      if (!bySource.has(key)) {
        bySource.set(key, {
          key,
          drugName: t.drugName,
          snomed: t.snomed,
          cleanName: t.cleanName,
          fromPharmacyId: t.fromPharmacyId,
          fromName: t.fromName,
          packSize: t.packSize,
          availablePacks: t.fromRow?.certainExcessPacks ?? 0,
          allocations: [],
          packs: 0,
          value: 0,
        });
      }
      const g = bySource.get(key);
      g.allocations.push({
        toPharmacyId: t.toPharmacyId,
        toName: t.toName,
        packs: t.packs,
        needPacks: t.needPacks,
      });
      g.packs += t.packs;
      g.value += t.value;
    }
    return [...bySource.values()].sort((a, b) => b.value - a.value);
  }, [model]);

  /* Warehouse retrieval, in the shape the raise machinery already understands.
     One request per branch and product, with a single destination - the
     warehouse - so every cap, preview and email path below works unchanged.

     The destination carries no pharmacyId. That is the convention the model
     already reads: buildPendingMap treats a transfer with no toPharmacyId as
     stock LEAVING the estate rather than arriving at a branch, so a pending
     retrieval correctly stops counting as cover anywhere. */
  const warehouseGroups = useMemo(() => {
    return model.warehouse
      .map((w) => ({
        key: `${w.pharmacyId}|${w.cleanName}|wh`,
        drugName: w.drugName,
        snomed: w.snomed,
        cleanName: w.cleanName,
        fromPharmacyId: w.pharmacyId,
        fromName: w.pharmacyName,
        packSize: w.packSize,
        availablePacks: w.packs,
        toWarehouse: true,
        allocations: [{
          toPharmacyId: WAREHOUSE_ID,
          toName: WAREHOUSE_NAME,
          packs: w.packs,
          needPacks: 0,
        }],
        packs: w.packs,
        value: w.value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [model]);

  // Whichever set the dialog is currently reviewing.
  const activeGroups = raiseMode === "warehouse" ? warehouseGroups : raiseGroups;

  // The packs currently set for one destination, defaulting to the suggestion.
  const allocFor = (g, a) => {
    const k = `${g.key}|${a.toPharmacyId}`;
    return raiseAlloc[k] === undefined ? a.packs : raiseAlloc[k];
  };

  const setAllocFor = (g, a, raw) => {
    const want = Math.max(0, Math.floor(Number(raw) || 0));
    // Capped against what is left after the OTHER destinations, so the total
    // can never exceed what the source branch actually has spare, however the
    // boxes are filled in.
    const others = g.allocations
      .filter((x) => x.toPharmacyId !== a.toPharmacyId)
      .reduce((t, x) => t + allocFor(g, x), 0);
    const capped = Math.min(want, Math.max(0, g.availablePacks - others));
    setRaiseAlloc((m) => ({ ...m, [`${g.key}|${a.toPharmacyId}`]: capped }));
  };

  const allocationsOf = (g) =>
    g.allocations
      .map((a) => ({
        toPharmacyId: a.toPharmacyId,
        toName: a.toName,
        packs: allocFor(g, a),
        needPacks: a.needPacks,
      }))
      .filter((a) => a.packs > 0);

  const totalOf = (g) => allocationsOf(g).reduce((t, a) => t + a.packs, 0);

  const previewEmail = async (g, which) => {
    const allocations = allocationsOf(g);
    if (!allocations.length) { alert("Allocate at least one pack first."); return; }
    try {
      const res = await fetch(`${API}/previewTransferEmails`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          groupId, from: g.fromName, drug: g.drugName,
          packSize: g.packSize, allocations, note: raiseOpts.note,
          ...(g.toWarehouse ? { destination: "warehouse", toName: WAREHOUSE_NAME } : {}),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) { alert(`Preview failed: ${d.error || `HTTP ${res.status}`}`); return; }

      if (which === "recipient") {
        // One email per receiving branch, each with its own quantity and its
        // own link, so they are stacked with a heading rather than merged -
        // seeing them side by side is the point, and a merged preview would
        // suggest the branches get one shared message.
        const list = d.recipients || [];
        if (!list.length) {
          alert("Nothing goes to a receiving branch on this line. A warehouse retrieval has no branch at the other end to tell.");
          return;
        }
        setPreview({
          title: list.length === 1
            ? `Email to ${list[0].toName}`
            : `Emails to ${list.length} receiving branches`,
          html: list.map((r) => `
            <div style="margin:0 0 10px;padding:8px 12px;background:#eef2f7;border-left:4px solid #2b7de9;
                        font:600 14px/1.4 Arial,sans-serif;color:#1f2933">
              To ${r.toName} &mdash; ${r.subject}
            </div>${r.html}`).join('<hr style="margin:26px 0;border:none;border-top:1px dashed #cbd5e1">'),
        });
        return;
      }

      setPreview({
        title: which === "branch" ? "Email to the sending branch" : "Email to the driver",
        html: which === "branch" ? d.branch?.html : d.driver?.html,
      });
    } catch (e) {
      alert(`Preview failed: ${e.message}`);
    }
  };

  const raiseOne = async (g) => {
    if (raiseDone[g.key]?.ok) return null;
    const allocations = allocationsOf(g);
    if (!allocations.length) {
      setRaiseDone((m) => ({ ...m, [g.key]: { ok: false, text: "nothing allocated" } }));
      return null;
    }
    try {
      const res = await fetch(`${API}/createAllocatedTransfer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          groupId,
          fromPharmacyId: g.fromPharmacyId,
          drug: g.drugName,
          snomed: g.snomed,
          packSize: g.packSize,
          availablePacks: g.availablePacks,
          allocations,
          // Named explicitly as well as implied by a null toPharmacyId, so the
          // backend does not have to infer intent from a missing field.
          ...(g.toWarehouse ? { destination: "warehouse", toName: WAREHOUSE_NAME } : {}),
          emailBranch: raiseOpts.emailBranch,
          emailDriver: raiseOpts.emailDriver,
          emailRecipients: raiseOpts.emailRecipients,
          driverEmailAddress: raiseOpts.driverEmail,
          note: raiseOpts.note,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) {
        setRaiseDone((m) => ({ ...m, [g.key]: { ok: false, text: d.error || `HTTP ${res.status}` } }));
        return null;
      }
      setRaiseDone((m) => ({
        ...m,
        [g.key]: { ok: true, text: `${d.reference} — ${d.totalPacks} pack(s)`, failures: d.failures || [] },
      }));
      return d;
    } catch (e) {
      setRaiseDone((m) => ({ ...m, [g.key]: { ok: false, text: e.message || "request failed" } }));
      return null;
    }
  };

  const raiseSingle = async (g) => {
    if (raiseOpts.emailDriver && !String(raiseOpts.driverEmail).trim()) {
      alert("Enter the driver's email address, or untick 'Email delivery driver'.");
      return;
    }
    setRaising({ done: 0, total: 1 });
    await raiseOne(g);
    setRaising(null);
    await loadAll();
  };

  const processAll = async () => {
    const outstanding = activeGroups.filter((g) => !raiseDone[g.key]?.ok && totalOf(g) > 0);
    if (!outstanding.length) return;
    if (raiseOpts.emailDriver && !String(raiseOpts.driverEmail).trim()) {
      alert("Enter the driver's email address, or untick 'Email delivery driver'.");
      return;
    }
    const packs = outstanding.reduce((a, g) => a + totalOf(g), 0);
    if (!window.confirm(raiseMode === "warehouse"
      ? `Raise ${outstanding.length} warehouse retrieval${outstanding.length === 1 ? "" : "s"}, `
        + `bringing ${packs} pack(s) back from the branches?`
      : `Raise ${outstanding.length} transfer${outstanding.length === 1 ? "" : "s"}, `
        + `moving ${packs} pack(s)?`
    )) return;

    setRaising({ done: 0, total: outstanding.length });
    // Sequential: each one writes to stock, and a burst of concurrent writes
    // against the same branch is how double-counting starts.
    for (let i = 0; i < outstanding.length; i++) {
      await raiseOne(outstanding[i]);
      setRaising({ done: i + 1, total: outstanding.length });
    }
    setRaising(null);
    await loadAll();
  };

  /* --- the transfers list, same actions as the detail screen ------------ */

  const transferAction = async (t, what) => {
    const ids = (t.sourceIds && t.sourceIds.length) ? t.sourceIds : (t._id ? [t._id] : []);
    if (!ids.length) {
      alert(`This row has no transfer id attached, so it cannot be ${what}d. Refresh and try again.`);
      return;
    }
    // Both actions get a confirmation, and both say HOW MANY REQUESTS they
    // reach. The list groups by sending branch and destination only, so one
    // displayed row can stand for several separate transfer requests raised on
    // different days for different drugs - and the button acts on all of them.
    // A confirmation naming one drug while quietly deleting four is worse than
    // no confirmation at all.
    const dest = t.toPharmacyName || WAREHOUSE_NAME;
    const items = (t.items || []).map((it) => `${it.drug} (${it.qty})`).join(", ") || "this transfer";
    const many = ids.length > 1
      ? `\n\nThis row covers ${ids.length} separate transfer requests`
        + `${t.reference ? ` (${t.reference})` : ""}, and ALL of them will be `
        + `${what === "delete" ? "deleted" : "completed"}.`
      : "";

    if (what === "delete") {
      if (!window.confirm(
        `Delete the transfer of ${items} from ${t.fromPharmacyName} to ${dest}?${many}`
      )) return;
    } else {
      // Completing now moves stock at both ends, which it did not used to, so
      // it needs asking about just as much as deleting does.
      if (!window.confirm(
        `Complete the transfer of ${items} from ${t.fromPharmacyName} to ${dest}?`
        + `\n\nThe packs come off ${t.fromPharmacyName}'s shelf and onto ${dest}'s.${many}`
      )) return;
    }
    try {
      const path = what === "delete" ? "deleteTransfer" : "completeTransfer";
      const res = await fetch(`${API}/${path}/${encodeURIComponent(ids.join(","))}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        alert(`Could not ${what}: HTTP ${res.status}${body ? `\n\n${body.slice(0, 200)}` : ""}`);
        return;
      }
      // Everything reloads: completing or deleting a transfer changes what is
      // pending, and every figure on this screen is built on that. The
      // dialogue's own list is refreshed too, or it would keep showing the
      // state the row was in before the button was pressed.
      await loadAll();
      if (showTransfers) await loadTransfersFull();
    } catch (e) {
      alert(`Could not ${what}: ${e.message}`);
    }
  };

  const loadTransfersFull = async () => {
    setTransfersFullLoading(true);
    try {
      const res = await fetch(`${API}/getTransfers?groupId=${groupId}&includeCompleted=1`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const d = await res.json().catch(() => null);
      setTransfersFull(Array.isArray(d) ? d : []);
    } catch (_) {
      // Falls back to the pending-only list rather than showing nothing.
      setTransfersFull(null);
    } finally {
      setTransfersFullLoading(false);
    }
  };

  const openTransfers = () => { setShowTransfers(true); loadTransfersFull(); };

  const fmtWhen = (d) => {
    if (!d) return "";
    const t = new Date(d);
    return Number.isNaN(t.getTime()) ? "" : t.toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  /* --- guide ------------------------------------------------------------- */

  const GUIDE_MS = 9000;

  useEffect(() => {
    if (guideStep < 0) return;
    if (guideStep >= GUIDE.length) { setGuideStep(-1); setGuidePaused(false); setOpenPanel(null); return; }

    // Open whichever panel is being described. A guide that points at a
    // collapsed panel and talks about the list inside it is asking people to
    // imagine the thing being explained.
    const panelSteps = ["transfer", "warehouse", "unsure", "idle"];
    if (panelSteps.includes(GUIDE[guideStep].ref)) setOpenPanel(GUIDE[guideStep].ref);

    // The last four steps explain a panel that only exists once a branch is
    // open, so the guide opens one itself - and expands a line for the step
    // that describes clicking, rather than telling someone to click and
    // showing them nothing.
    if (GUIDE[guideStep].ref === "detail") {
      const first = branchCards.find((b) => b.rows.length > 0);
      if (first) {
        if (!selBranch) setSelBranch(first.pharmacyId);
        const target = branchCards.find((b) => b.pharmacyId === (selBranch || first.pharmacyId));
        const withStock = target?.rows.find((r) => r.qtyOnShelf > 0);
        if (GUIDE[guideStep].title.startsWith("Click a bar") && withStock) {
          setSelLine(`${withStock.pharmacyId}|${withStock.cleanName}`);
        }
      }
    }

    const t = setTimeout(() => {
      regions[GUIDE[guideStep].ref]?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);   // let a newly opened panel mount before scrolling to it

    if (guidePaused) return () => clearTimeout(t);
    const advance = setTimeout(() => setGuideStep((s) => s + 1), GUIDE_MS);
    return () => { clearTimeout(t); clearTimeout(advance); };
  }, [guideStep, guidePaused]); // eslint-disable-line react-hooks/exhaustive-deps

  const lit = (name) => guideStep >= 0 && GUIDE[guideStep]?.ref === name;
  const litStyle = (name) => lit(name)
    ? { outline: "3px solid #2b7de9", outlineOffset: 3, position: "relative", zIndex: 1310 }
    : {};

  /* --- pieces ------------------------------------------------------------ */

  const Panel = ({ id, title, help, packs, value, lines, colour, children }) => (
    <div
      ref={regions[id]}
      onClick={() => setOpenPanel(openPanel === id ? null : id)}
      style={{
        flex: "1 1 300px", cursor: "pointer", background: "#fff",
        border: `2px solid ${colour}`, borderRadius: 10, padding: "14px 16px",
        ...litStyle(id),
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", fontWeight: 700, color: "#1f2937" }}>
          {title}<span onClick={(e) => e.stopPropagation()}><Help text={help} wide /></span>
        </div>
        <span style={{ color: colour, fontSize: "0.8em", fontWeight: 700 }}>
          {openPanel === id ? "▲ hide" : "▼ show"}
        </span>
      </div>
      <div style={{ fontSize: "2em", fontWeight: 700, color: colour, marginTop: 6, lineHeight: 1.1 }}>
        {money(value)}
      </div>
      <div style={{ fontSize: "0.86em", color: "#475569" }}>
        {packs} pack{packs === 1 ? "" : "s"} · {lines} line{lines === 1 ? "" : "s"}
      </div>
      {children}
    </div>
  );

  const th = { textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: "6px 8px", whiteSpace: "nowrap" };
  const td = { borderBottom: "1px solid #f1f5f9", padding: "6px 8px" };

  if (loading) return <div style={{ padding: 30 }}>Loading the estate…</div>;

  return (
    <div style={{ padding: "18px 22px", minHeight: "100vh", background: "#f7f9fc" }}>

      {/* ---- title ---- */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ margin: 0 }}>Estate stock and warehouse</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* The way down. "Analysis and transfer" in the rail lands here, so
              this is the route to the line-by-line screen underneath it. */}
          {onOpenDetail && (
            <button onClick={onOpenDetail}
              title="Down to the line-by-line detail: every branch and product, the transfer engine and the exports."
              style={{ padding: "8px 14px", fontWeight: 600 }}>
              Detailed
            </button>
          )}
          <button onClick={() => { setGuidePaused(false); setGuideStep(0); }}
            style={{ padding: "8px 14px", fontWeight: 600 }}>
            Show guide
          </button>
          {/* The usage tools, shared with the detail screen rather than copied.
              They belong here because this screen depends entirely on usage:
              a branch with none is excluded from every figure on it, and this
              is where you would notice. */}
          <UsageTools
            groupId={groupId}
            members={members}
            defaultPharmacyId={selBranch || ""}
            masked={masked}
            inventory={inventoryData}
            onDataChanged={loadAll}
          />
          <button onClick={openTransfers} style={{ padding: "8px 14px" }}>
            View transfers{pendingTransferCount > 0 ? ` (${pendingTransferCount})` : ""}
          </button>
          <button onClick={loadAll} style={{ padding: "8px 14px" }}>Refresh</button>
          <MaskToggleButton />
        </div>
      </div>

      {err && (
        <div style={{ margin: "12px 0", padding: "9px 11px", background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b" }}>
          {err}
        </div>
      )}

      {/* ---- the months control: the one number that drives everything ---- */}
      <div ref={regions.cover} style={{
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        background: "#fff", border: "1px solid #dfe3e8", borderRadius: 10,
        padding: "12px 16px", margin: "14px 0", ...litStyle("cover"),
      }}>
        <span style={{ fontWeight: 600, color: "#334155" }}>Keep enough stock for</span>
        <input
          type="range" min="1" max="9" step="1" value={monthsAhead}
          onChange={(e) => setMonthsAhead(Number(e.target.value))}
          style={{ flex: "1 1 220px", maxWidth: 420 }}
        />
        <span style={{ fontSize: "1.3em", fontWeight: 700, minWidth: 96 }}>
          {monthsAhead} month{monthsAhead === 1 ? "" : "s"}
        </span>
        <Help wide text="Stock divided by monthly usage tells you how long a shelf lasts. Anything beyond the months you set here is spare. Move it left and more stock becomes spare; move it right and less does." />
      </div>

      {/* ---- the two action panels ---- */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
        <Panel
          id="transfer" title="Eligible for transfer" colour="#1d9e75"
          packs={model.totals.transferPacks} value={model.totals.transferValue}
          lines={model.transfers.length}
          help="Spare stock at one branch that another branch is genuinely short of, so it can move directly rather than going back to the warehouse. Worked out product by product, serving the shortest branch first. Only stock with usage we trust is counted, and anything already on a pending transfer is treated as gone."
        >
          <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setRaiseMode("transfer"); setRaiseDone({}); setRaiseAlloc({}); setRaiseOpen(true); }}
              disabled={!model.transfers.length || !!raising}
              style={{
                padding: "8px 14px", fontWeight: 700, border: "none", borderRadius: 5,
                background: model.transfers.length && !raising ? "#1d9e75" : "#a9cfc1",
                color: "#fff", cursor: model.transfers.length && !raising ? "pointer" : "default",
              }}
            >
              {raising ? `Raising ${raising.done} of ${raising.total}…` : "Raise transfers…"}
            </button>
          </div>
        </Panel>

        <Panel
          id="warehouse" title="Eligible for warehouse retrieval" colour="#BA7517"
          packs={model.totals.warehousePacks} value={model.totals.warehouseValue}
          lines={model.warehouse.length}
          help="Spare stock that no branch is short of, so the only place for it is the warehouse. It is whatever is left after every branch shortage above has been covered — which is why this figure changes when transfers are raised."
        >
          <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setRaiseMode("warehouse"); setRaiseDone({}); setRaiseAlloc({}); setRaiseOpen(true); }}
              disabled={!model.warehouse.length || !!raising}
              title="Raise the same kind of request as a branch-to-branch transfer, but sending the stock back to the warehouse."
              style={{
                padding: "8px 14px", fontWeight: 700, border: "none", borderRadius: 5,
                background: model.warehouse.length && !raising ? "#BA7517" : "#e0cba6",
                color: "#fff", cursor: model.warehouse.length && !raising ? "pointer" : "default",
              }}
            >
              {raising ? `Raising ${raising.done} of ${raising.total}…` : "Raise warehouse retrieval…"}
            </button>
          </div>
        </Panel>

        <Panel
          id="unsure" title="Held back — usage unsure" colour="#888780"
          packs={model.totals.unsurePacks} value={model.totals.unsureValue}
          lines={model.unsure.length}
          help="Stock that looks spare, but only because of usage we cannot rely on — a single month of data, or usage that swings wildly month to month. It is deliberately excluded from both panels and never moved automatically. Worth checking by hand before acting."
        />

        <Panel
          id="idle" title="Idle stock" colour="#6b4fa8"
          packs={idleStock.packs} value={idleStock.value}
          lines={idleStock.lines.length}
          help="Everything spare across the whole estate that has not been committed to a transfer — the widest of these figures and a superset of the other three. It includes branches excluded for out-of-date data and lines held back on unreliable usage, so it is larger than what you can act on today. Stock already on a pending transfer is not counted, because it is on its way somewhere."
        >
          {idleStock.stuckValue > 0 && (
            <div style={{ fontSize: "0.8em", color: "#6b4fa8", marginTop: 6 }}>
              {money(idleStock.stuckValue)} of it cannot be acted on yet
            </div>
          )}
        </Panel>

        {/* Estate-wide top lines — not a spare-stock panel like the four
            above, so it sits beside them as a plain button rather than
            joining their click-to-open pattern. */}
        <div style={{ display: "flex", alignItems: "stretch" }}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowTop200((v) => !v); }}
            title="The estate's biggest lines by value or packet count over the last three months, one column per branch."
            style={{
              padding: "14px 18px", fontWeight: 700, border: "2px solid #2b7de9", borderRadius: 10,
              background: showTop200 ? "#2b7de9" : "#fff", color: showTop200 ? "#fff" : "#2b7de9",
              cursor: "pointer",
            }}
          >
            Top 200 lines {showTop200 ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {showTop200 && (
        <EstateTopLines
          rows={rows}
          members={members}
          masked={masked}
          orgLabel={localStorage.getItem("organisationName") || ""}
          usageWindows={usageWindows}
        />
      )}

      {/* ---- drill-downs ---- */}
      {openPanel === "transfer" && (
        <div style={{ background: "#fff", border: "1px solid #dfe3e8", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 700 }}>
            Branch to branch — {model.transfers.length} line(s)
          </div>
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9em" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9" }}>
                <tr><th style={th}>Drug</th><th style={th}>From</th><th style={th}>To</th>
                  <th style={{ ...th, textAlign: "right" }} title="What the destination branch is short of.">Short by</th>
                  <th style={{ ...th, textAlign: "right" }}>Packs</th>
                  <th style={{ ...th, textAlign: "right" }}>Value</th></tr>
              </thead>
              <tbody>
                {model.transfers.map((t, i) => (
                  <tr key={i}>
                    <td style={td}>
                      {t.drugName}
                      {t.why && (
                        <div style={{ fontSize: "0.82em", color: "#64748b", marginTop: 2 }}>{t.why}</div>
                      )}
                    </td>
                    <td style={td}>{displayName(t.fromName, masked)}</td>
                    <td style={td}>
                      {displayName(t.toName, masked)}
                      {t.destConfidence === "low" && (
                        <span title="This branch's need is based on usage we are not sure of."
                          style={{ marginLeft: 6, fontSize: "0.8em", color: "#888780" }}>· need unsure</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right", color: "#64748b" }}>
                      {t.needPacks}
                      {t.shortAfter > 0 && (
                        <span title="No branch has enough spare to cover the rest."
                          style={{ color: "#a05a00" }}> · {t.shortAfter} left short</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{t.packs}</td>
                    <td style={{ ...td, textAlign: "right" }}>{money(t.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openPanel === "idle" && (
        <div style={{ background: "#fff", border: "1px solid #dfe3e8", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 700 }}>
            Spare and uncommitted — {idleStock.lines.length} line(s)
            <span style={{ fontWeight: 400, color: "#64748b", fontSize: "0.85em" }}>
              {" "}· stock already on a pending transfer is deducted before the spare figure ·
              includes branches and lines the other panels leave out
            </span>
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9em" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9" }}>
                <tr>
                  <th style={th}>Drug</th><th style={th}>Pharmacy</th>
                  <th style={{ ...th, textAlign: "right" }} title="Packs counted on the shelf.">On shelf</th>
                  <th style={{ ...th, textAlign: "right" }} title="Already on a pending transfer, and deducted before the spare figure is worked out.">Committed</th>
                  <th style={{ ...th, textAlign: "right" }} title="What is left once the branch's own cover period and any pending transfer are taken off.">Spare</th>
                  <th style={{ ...th, textAlign: "right" }}>Value</th>
                  <th style={th}>Can it move?</th>
                </tr>
              </thead>
              <tbody>
                {idleStock.lines.map((l, i) => (
                  <tr key={i} style={{ background: l.actionable ? "#fff" : "#fbfaff" }}>
                    <td style={td}>{l.row.drugName}</td>
                    <td style={td}>{displayName(l.row.pharmacyName, masked)}</td>
                    {/* The working, not just the answer. A row reading "5 spare"
                        on a shelf of 8 with 2 already promised looks wrong until
                        the deduction is on screen next to it. */}
                    <td style={{ ...td, textAlign: "right", color: "#64748b" }}>
                      {Math.round(l.row.packetsOnShelf ?? (l.row.qtyOnShelf / (l.row.packSize || 1)))} pk
                    </td>
                    <td style={{ ...td, textAlign: "right", color: l.row.pendingOut > 0 ? "#8a4b06" : "#cbd5e1" }}>
                      {l.row.pendingOut > 0
                        ? `−${Math.round(l.row.pendingOut / (l.row.packSize || 1))} pk`
                        : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{l.packs} pk</td>
                    <td style={{ ...td, textAlign: "right" }}>{money(l.value)}</td>
                    <td style={{ ...td, color: l.actionable ? "#1d9e75" : "#6b4fa8" }}
                        title={!l.row.usable ? l.row.branchWhy : l.row.confidence?.why}>
                      {l.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(openPanel === "warehouse" || openPanel === "unsure") && (
        <div style={{ background: "#fff", border: "1px solid #dfe3e8", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontWeight: 700 }}>
            {openPanel === "warehouse" ? "Nobody needs these" : "Not sure enough to move these"}
          </div>
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9em" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9" }}>
                <tr><th style={th}>Drug</th><th style={th}>Pharmacy</th>
                  <th style={{ ...th, textAlign: "right" }}>Packs</th>
                  <th style={{ ...th, textAlign: "right" }}>Value</th>
                  {openPanel === "unsure" && <th style={th}>Why held back</th>}</tr>
              </thead>
              <tbody>
                {(openPanel === "warehouse" ? model.warehouse : model.unsure).map((w, i) => (
                  <tr key={i}>
                    <td style={td}>{w.drugName || w.row.drugName}</td>
                    <td style={td}>{displayName(w.pharmacyName || w.row.pharmacyName, masked)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{w.packs}</td>
                    <td style={{ ...td, textAlign: "right" }}>{money(w.value)}</td>
                    {openPanel === "unsure" && <td style={{ ...td, color: "#666" }}>{w.reason}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- branches ---- */}
      <div ref={regions.branches} style={{ ...litStyle("branches") }}>
        <h2 style={{ margin: "0 0 4px" }}>
          Branches
          <Help text="One card per branch in the group, counting its product lines: short of stock, about right, spare, or unsure. Branches with nothing loaded are shown greyed out — they take no part in any figure on this screen. Click a card with data to see its individual products." />
        </h2>
        {/* Coverage stated plainly. A handful of branches carrying every figure
            on the screen is something the reader has to know before acting. */}
        <div style={{ fontSize: "0.85em", color: staleCount ? "#a03030" : "#475569", marginBottom: 8 }}>
          {usableCount} of {branchCards.length} branches have usable data
          {staleCount > 0 && ` — ${staleCount} excluded from every figure on this screen`}
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: "0.78em", color: "#475569", marginBottom: 10 }}>
          <span><b style={{ color: "#1d9e75" }}>▬</b> up to date — last three months</span>
          <span><b style={{ color: "#BA7517" }}>▬</b> one month behind — still used</span>
          <span><b style={{ color: "#E24B4A" }}>▬</b> out of date — excluded from everything</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
          {branchCards.map((b) => {
            const tot = b.short + b.right + b.spare + b.unsure || 1;
            const seg = (c, v) => v ? <div key={c} style={{ width: `${v / tot * 100}%`, background: c }} /> : null;
            const on = selBranch === b.pharmacyId;
            // The border is the branch's data, not its stock. Broad and solid so
            // it reads before anything inside the card does: green means the
            // last three months are in, amber means one month behind, red means
            // this branch is taking no part in any figure on the screen.
            const edge = b.status === "green" ? "#1d9e75" : b.status === "amber" ? "#BA7517" : "#E24B4A";
            const stale = b.status === "red";

            if (stale) {
              return (
                <div key={b.pharmacyId}
                  title={`Excluded: ${b.why}. This branch contributes nothing to the transfer, warehouse or held-back figures.`}
                  style={{
                    background: "#fdf6f6", borderRadius: 10, padding: "12px 14px",
                    border: `4px solid ${edge}`, color: "#7a3b3b",
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                    <strong style={{ color: "#8a2f2f" }}>{displayName(b.pharmacyName, masked)}</strong>
                    <span style={{ fontSize: "0.8em", fontWeight: 700, color: "#a32d2d" }}>excluded</span>
                  </div>
                  <div style={{ fontSize: "0.8em", lineHeight: 1.45 }}>{b.why}</div>
                  <div style={{ marginTop: 6, fontSize: "0.78em", color: "#94564f" }}>
                    counts towards nothing on this screen
                  </div>
                </div>
              );
            }

            return (
              <div key={b.pharmacyId}
                onClick={() => { setSelBranch(on ? null : b.pharmacyId); setLinePage(0); setLineSearch(""); }}
                title={b.why}
                style={{
                  background: "#fff", cursor: "pointer", borderRadius: 10, padding: "12px 14px",
                  border: `4px solid ${edge}`,
                  outline: on ? "2px solid #2b7de9" : "none", outlineOffset: 2,
                }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <strong>{displayName(b.pharmacyName, masked)}</strong>
                  <span style={{ fontSize: "0.84em", color: "#475569" }}>
                    {b.spareValue > 0 ? `${money(b.spareValue)} spare` : "nothing spare"}
                    {b.status === "amber" && (
                      <span style={{ color: "#BA7517", fontWeight: 600 }}> · a month behind</span>
                    )}
                  </span>
                </div>
                <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: "#eef2f7" }}>
                  {seg("#E24B4A", b.short)}{seg("#639922", b.right)}{seg("#BA7517", b.spare)}{seg("#888780", b.unsure)}
                </div>
                <div style={{ fontSize: "0.76em", color: "#94a3b8", marginTop: 6 }}
                  title="The three months this branch's usage figures are actually averaged over. Falls back a month if the newest one is not yet fully reported.">
                  Months available: {monthsLabel(usageWindows[b.pharmacyId]?.months || [])}
                </div>
                {/* Pills, not footnotes. These were grey 0.78em text at the end
                    of a line, sitting under a greyed-out "none" that pulled the
                    eye away - which made a branch with stock arriving look
                    exactly like one with nothing happening. Only the direction
                    that actually has movement is shown; "none" earns no space. */}
                {(b.outLines > 0 || b.inLines > 0) && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {b.outLines > 0 && (
                      <span title={`${b.outLines} line(s) promised away and already deducted from this branch's figures.`}
                        style={{
                          fontSize: "0.78em", fontWeight: 700, padding: "3px 8px", borderRadius: 11,
                          background: "#fdf1e0", color: "#8a4b06", border: "1px solid #e8c79a",
                        }}>
                        ↗ {Math.round(b.outPacks)} pk out pending
                      </span>
                    )}
                    {b.inLines > 0 && (
                      <span title={`${b.inLines} line(s) on the way here and already counted as cover.`}
                        style={{
                          fontSize: "0.78em", fontWeight: 700, padding: "3px 8px", borderRadius: 11,
                          background: "#e6f0fb", color: "#14508f", border: "1px solid #a9c9ec",
                        }}>
                        ↘ {Math.round(b.inPacks)} pk in pending
                      </span>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8, fontSize: "0.8em", color: "#475569" }}>
                  {b.short > 0 && <span>🔴 {b.short} short</span>}
                  {b.right > 0 && <span>🟢 {b.right} right</span>}
                  {b.spare > 0 && <span>🟠 {b.spare} spare</span>}
                  {b.unsure > 0 && <span>⚪ {b.unsure} unsure</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---- one branch's shelves ---- */}
      {selBranch && (
        <div ref={regions.detail} style={{ marginTop: 14, ...litStyle("detail") }}>
          {(() => {
            const b = branchCards.find((x) => x.pharmacyId === selBranch);
            if (!b) return null;
            return (
              <BranchStockTabs
                branch={b}
                allRows={rows}
                monthsAheadDefault={monthsAhead}
                masked={masked}
                usageWindow={usageWindows[b.pharmacyId]}
              />
            );
          })()}
        </div>
      )}

      {/* ---- review before raising ---- */}
      {raiseOpen && (() => {
        const driverEmailMissing = raiseOpts.emailDriver && !String(raiseOpts.driverEmail).trim();
        const noEmails = !raiseOpts.emailBranch && !raiseOpts.emailDriver && !raiseOpts.emailRecipients;
        const toWarehouse = raiseMode === "warehouse";
        return (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1400 }}
            onClick={() => !raising && setRaiseOpen(false)} />
          <div style={{
            position: "fixed", top: "5vh", left: "50%", transform: "translateX(-50%)",
            width: 1000, maxWidth: "94vw", maxHeight: "90vh", overflowY: "auto", zIndex: 1410,
            background: "#fff", border: "1px solid #cbd5e1", borderRadius: 10,
            boxShadow: "0 16px 40px rgba(15,23,42,0.35)", padding: "16px 18px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "1.1em" }}>
                  {toWarehouse ? "Warehouse retrievals ready to raise" : "Transfers ready to raise"}
                </div>
                <div style={{ fontSize: "0.85em", color: "#475569", marginTop: 2 }}>
                  {activeGroups.length} request{activeGroups.length === 1 ? "" : "s"} ·{" "}
                  {activeGroups.reduce((a, g) => a + g.packs, 0)} pack(s) ·{" "}
                  {money(activeGroups.reduce((a, g) => a + g.value, 0))}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* Each row can go on its own, exactly as on the detail screen.
                    This is the one that does the lot. */}
                <button
                  onClick={processAll}
                  title={driverEmailMissing ? "Enter the driver's address, or untick 'Email delivery driver'." : undefined}
                  disabled={!!raising || driverEmailMissing || activeGroups.every((g) => raiseDone[g.key]?.ok)}
                  style={{
                    padding: "9px 16px", fontWeight: 700, border: "none", borderRadius: 5,
                    background: raising || driverEmailMissing
                      ? (toWarehouse ? "#e0cba6" : "#a9cfc1")
                      : (toWarehouse ? "#BA7517" : "#1d9e75"),
                    color: "#fff",
                    cursor: raising || driverEmailMissing ? "default" : "pointer",
                  }}
                >
                  {raising ? `Processing ${raising.done} of ${raising.total}…` : "Process all"}
                </button>
                <button onClick={() => setRaiseOpen(false)} disabled={!!raising} style={{ padding: "9px 14px" }}>
                  Close
                </button>
              </div>
            </div>

            {/* Options, same as the detail screen's dialog so a transfer raised
                here arrives looking identical to one raised there. */}
            <div style={{ margin: "12px 0", padding: "10px 12px", background: "#f6f8fa", borderRadius: 6 }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.9em" }}>
                  <input type="checkbox" checked={raiseOpts.emailBranch}
                    onChange={(e) => setRaiseOpts((o) => ({ ...o, emailBranch: e.target.checked }))} />
                  {toWarehouse ? "Email each branch with the retrieval request" : "Email each branch with the request"}
                </label>
                {/* The receiving branch. A warehouse retrieval has nobody at the
                    other end, so the option is shown disabled and says why
                    rather than silently doing nothing when ticked. */}
                <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.9em",
                                opacity: toWarehouse ? 0.5 : 1 }}
                  title={toWarehouse
                    ? "A warehouse retrieval has no receiving branch to tell."
                    : "Each receiving branch is emailed what is coming, with a link to confirm what arrived."}>
                  <input type="checkbox" checked={raiseOpts.emailRecipients && !toWarehouse}
                    disabled={toWarehouse}
                    onChange={(e) => setRaiseOpts((o) => ({ ...o, emailRecipients: e.target.checked }))} />
                  Email each receiving branch with a confirmation link
                </label>
                <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.9em" }}>
                  <input type="checkbox" checked={raiseOpts.emailDriver}
                    onChange={(e) => setRaiseOpts((o) => ({ ...o, emailDriver: e.target.checked }))} />
                  Email delivery driver
                </label>
                {raiseOpts.emailDriver && (
                  <input type="email" placeholder="driver@example.com" value={raiseOpts.driverEmail}
                    onChange={(e) => setRaiseOpts((o) => ({ ...o, driverEmail: e.target.value }))}
                    style={{
                      padding: "6px 9px", minWidth: 220,
                      border: driverEmailMissing ? "1.5px solid #E24B4A" : "1px solid #cbd5e1",
                    }} />
                )}
                <input type="text" placeholder="Note for the branch and driver (optional)"
                  value={raiseOpts.note}
                  onChange={(e) => setRaiseOpts((o) => ({ ...o, note: e.target.value }))}
                  style={{ padding: "6px 9px", flex: "1 1 260px" }} />
              </div>

              {/* What will actually happen, in one line. A transfer that goes
                  out silently is a real outcome and should never be reached by
                  accident, so it is stated rather than left to be inferred
                  from two unticked boxes. */}
              <div style={{ marginTop: 8, fontSize: "0.85em",
                            color: noEmails ? "#a05a00" : driverEmailMissing ? "#b91c1c" : "#475569" }}>
                {driverEmailMissing
                  ? "Enter the driver's address, or untick 'Email delivery driver'."
                  : noEmails
                    ? `No emails will be sent — the ${toWarehouse ? "retrieval" : "transfer"} will be raised silently, and staff will only see it in the transfers list.`
                    : `On raising: ${[
                        raiseOpts.emailBranch && "the sending branch is emailed the pick list",
                        raiseOpts.emailRecipients && !toWarehouse && "each receiving branch is emailed a confirmation link",
                        raiseOpts.emailDriver && "the driver is emailed",
                      ].filter(Boolean).join(", ")}.`}
              </div>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88em" }}>
              <thead style={{ background: "#f1f5f9" }}>
                <tr>
                  <th style={th}>Drug</th>
                  <th style={th}>From</th>
                  <th style={th}>To</th>
                  <th style={{ ...th, textAlign: "right" }}>Packs</th>
                  <th style={{ ...th, textAlign: "right" }}>Value</th>
                  <th style={th}>Status</th>
                  <th style={th}>Raise / preview</th>
                </tr>
              </thead>
              <tbody>
                {activeGroups.map((g) => {
                  const done = raiseDone[g.key];
                  return (
                    <tr key={g.key} style={{ background: done?.ok ? "#f2faf6" : done ? "#fef4f4" : "#fff" }}>
                      <td style={td}>{g.drugName}</td>
                      <td style={td}>{displayName(g.fromName, masked)}</td>
                      <td style={td}>
                        {g.allocations.map((a, i) => {
                          const packs = allocFor(g, a);
                          return (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                              <input
                                type="number" min="0" max={g.availablePacks}
                                value={packs}
                                disabled={!!raising || done?.ok}
                                onChange={(e) => setAllocFor(g, a, e.target.value)}
                                style={{ width: 58, padding: "3px 5px", textAlign: "right" }}
                              />
                              <span>pk → {displayName(a.toName, masked)}</span>
                              {a.needPacks > packs && (
                                <span title="This branch is short by more than it is being sent."
                                  style={{ color: "#a05a00" }}>needs {a.needPacks}</span>
                              )}
                            </div>
                          );
                        })}
                        <div style={{ fontSize: "0.85em", color: "#64748b", marginTop: 2 }}>
                          {totalOf(g)} of {g.availablePacks} spare allocated
                        </div>
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>{totalOf(g)}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {money(g.packs > 0 ? (g.value / g.packs) * totalOf(g) : 0)}
                      </td>
                      <td style={{ ...td, color: done?.ok ? "#1d9e75" : done ? "#b91c1c" : "#94a3b8" }}>
                        {done ? done.text : "not raised"}
                        {done?.ok && done.failures?.length > 0 && (
                          <div style={{ color: "#a05a00", fontSize: "0.9em" }}>
                            not emailed: {done.failures.join(", ")}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "stretch" }}>
                          <button
                            onClick={() => raiseSingle(g)}
                            disabled={!!raising || done?.ok || totalOf(g) === 0 || driverEmailMissing}
                            style={{
                              padding: "5px 12px", fontWeight: 600, border: "none", borderRadius: 4,
                              background: done?.ok || totalOf(g) === 0 ? "#cbd5e1" : "#2b7de9", color: "#fff",
                              cursor: raising || done?.ok || totalOf(g) === 0 ? "default" : "pointer",
                            }}
                          >
                            {done?.ok ? "Raised" : done ? "Retry" : toWarehouse ? "Retrieve" : "Transfer"}
                          </button>
                          {!done?.ok && (
                            <div style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => previewEmail(g, "branch")}
                                disabled={totalOf(g) === 0}
                                title="See the email the SENDING branch would receive - the pick list and slip."
                                style={{ padding: "3px 7px", fontSize: "0.85em", flex: 1 }}>
                                Sender
                              </button>
                              {/* A warehouse retrieval has no receiving branch, so there is
                                  no third email to look at and the button says so rather
                                  than opening an empty window. */}
                              <button onClick={() => previewEmail(g, "recipient")}
                                disabled={totalOf(g) === 0 || g.toWarehouse}
                                title={g.toWarehouse
                                  ? "A warehouse retrieval has no receiving branch to tell."
                                  : "See the email each RECEIVING branch would receive, with its confirmation link."}
                                style={{ padding: "3px 7px", fontSize: "0.85em", flex: 1 }}>
                                Recipient
                              </button>
                              <button onClick={() => previewEmail(g, "driver")}
                                disabled={totalOf(g) === 0}
                                title="See the email the driver would receive."
                                style={{ padding: "3px 7px", fontSize: "0.85em", flex: 1 }}>
                                Driver
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ fontSize: "0.8em", color: "#64748b", marginTop: 10, lineHeight: 1.5 }}>
              {toWarehouse
                ? <>Each request covers one branch&rsquo;s spare stock of one product, going back to the warehouse
                    rather than to another branch. It is raised the same way, gets its own reference and branch
                    email, and appears in the same transfers list. Every pack is offered by default: lower the
                    figure to leave some on the shelf, or set it to zero to skip the line entirely. Only stock no
                    branch is short of reaches this panel, so nothing here is being taken from a branch that needs
                    it. Anything already raised is marked and cannot be sent twice.</>
                : <>Each request covers one branch&rsquo;s spare stock of one product, split across the branches that
                    need it &mdash; the same shape as raising it from the detail screen, and it gets its own
                    reference and branch email. The suggested split is a starting point: change any figure and the
                    total is capped at what that branch actually has spare. Set a destination to zero to leave it
                    out. Anything already raised is marked and cannot be sent twice.</>}
            </div>
          </div>
        </>
        );
      })()}

      {/* ---- transfers list ---- */}
      {showTransfers && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 1400 }}
            onClick={() => setShowTransfers(false)} />
          <div style={{
            position: "fixed", top: "5vh", left: "50%", transform: "translateX(-50%)",
            width: 1100, maxWidth: "95vw", maxHeight: "88vh", overflowY: "auto", zIndex: 1410,
            background: "#fff", border: "1px solid #cbd5e1", borderRadius: 10,
            boxShadow: "0 16px 40px rgba(15,23,42,0.35)", padding: "16px 18px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "1.1em" }}>Transfers</div>
                <div style={{ fontSize: "0.85em", color: "#475569" }}>
                  Raised transfers, including finished ones. A pending row is already taken out
                  of the sending branch and counted as cover at the receiving one.
                  <strong> Received</strong> is what the receiving branch confirmed through the
                  link in its email; a difference against what was sent is coloured.
                </div>
              </div>
              <button onClick={() => setShowTransfers(false)} style={{ padding: "7px 14px" }}>Close</button>
            </div>

            {(() => {
              const rows = transfersFull || transferData || [];
              return rows.length === 0 ? (
              <div style={{ color: "#64748b", padding: "12px 2px" }}>
                {transfersFullLoading ? "Loading…" : "No transfers."}
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.86em" }}>
                <thead style={{ background: "#f1f5f9" }}>
                  <tr>
                    <th style={th}>Date</th>
                    <th style={th}>From</th>
                    <th style={th}>To</th>
                    <th style={th}>Items</th>
                    <th style={th}>Picked</th>
                    <th style={th}>Received</th>
                    <th style={th}>Completed</th>
                    <th style={th}>Deleted</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t, i) => {
                    const rowKey = t._id || String(i);
                    // A row can merge several requests, so the variance shown is
                    // the sum across them - and a nil delivery on any leg is
                    // worth saying loudly rather than averaging away.
                    const variance = (t.received || []).reduce((a, r) => a + (Number(r.variance) || 0), 0);
                    const nilDelivery = (t.received || []).some((r) => r.nilDelivery);
                    return (
                    <React.Fragment key={rowKey}>
                    <tr style={{
                      background: nilDelivery ? "#fdf0f0" : t.deleted ? "#faf7f7" : variance ? "#fdf8ef" : "#fff",
                      opacity: t.deleted ? 0.6 : 1 }}>
                      <td style={td}>{fmtWhen(t.createdAt || t.date || t.timestamp)}</td>
                      <td style={td}>{displayName(t.fromPharmacyName, masked)}</td>
                      <td style={td}>
                        {t.toPharmacyName
                          ? displayName(t.toPharmacyName, masked)
                          : (t.destination === "warehouse" || !t.toPharmacyId)
                            ? <span style={{ color: "#BA7517", fontWeight: 600 }}>{WAREHOUSE_NAME}</span>
                            : "\u2014"}
                      </td>
                      <td style={td}>
                        {(t.items || []).map((it, n) => (
                          <div key={n}>
                            {it.drug} ({it.qty})
                            {it.toPharmacyName && !t.toPharmacyName && (
                              <span style={{ color: "#64748b" }}> → {displayName(it.toPharmacyName, masked)}</span>
                            )}
                          </div>
                        ))}
                      </td>
                      {/* Picked - what the sending branch scanned off its shelf.
                          Same shape as the detail screen: a count you can click
                          to see the lines behind it. */}
                      <td style={td}>
                        <span
                          style={{ cursor: (t.picked || []).length ? "pointer" : "default" }}
                          title={(t.picked || []).length ? "Show what was picked" : "Nothing picked yet"}
                          onClick={() => (t.picked || []).length &&
                            setOpenPicked((m) => ({ ...m, [rowKey]: !m[rowKey] }))}
                        >
                          📦 {(t.picked || []).length}
                        </span>
                        {openPicked[rowKey] && (
                          <div style={{ marginTop: 4, fontSize: "0.92em", color: "#475569" }}>
                            {(t.picked || []).map((p, n) => (
                              <div key={n}>
                                {p.drug} ({p.picked ?? p.qty ?? 0})
                                {p.note ? <span style={{ color: "#a05a00" }}> — {p.note}</span> : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Received - what the receiving branch confirmed through
                          the link in its email. A variance is the whole reason
                          the confirmation exists, so it is coloured and stated
                          on the closed row rather than hidden behind the click. */}
                      <td style={td}>
                        <span
                          style={{ cursor: (t.received || []).length ? "pointer" : "default",
                                   color: nilDelivery ? "#b91c1c" : variance ? "#a05a00" : undefined,
                                   fontWeight: nilDelivery || variance ? 700 : 400 }}
                          title={(t.received || []).length ? "Show what was confirmed" : "Not confirmed yet"}
                          onClick={() => (t.received || []).length &&
                            setOpenReceived((m) => ({ ...m, [rowKey]: !m[rowKey] }))}
                        >
                          📥 {(t.received || []).length}
                        </span>
                        {nilDelivery && (
                          <div style={{ color: "#b91c1c", fontWeight: 700, fontSize: "0.9em" }}>
                            nothing arrived
                          </div>
                        )}
                        {!nilDelivery && variance !== 0 && (
                          <div style={{ color: "#a05a00", fontWeight: 700, fontSize: "0.9em" }}>
                            {variance > 0 ? `+${variance}` : variance} vs sent
                          </div>
                        )}
                        {openReceived[rowKey] && (
                          <div style={{ marginTop: 4, fontSize: "0.92em", color: "#475569" }}>
                            {(t.received || []).map((r, n) => (
                              <div key={n} style={{ marginBottom: 3 }}>
                                {r.drug} ({r.received ?? r.qty ?? 0} of {r.sent ?? "?"})
                                {r.at ? <span style={{ color: "#94a3b8" }}> — {fmtWhen(r.at)}</span> : null}
                                {r.note ? <div style={{ color: "#a05a00" }}>“{r.note}”</div> : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>

                      <td style={td}>{t.status === "completed" ? `✅ ${fmtWhen(t.completedAt)}` : "❌"}</td>
                      <td style={td}>{t.deleted ? `✅ ${fmtWhen(t.deletedAt)}` : "❌"}</td>
                      <td style={td}>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          <button onClick={() => transferAction(t, "complete")}
                            disabled={t.deleted || t.status === "completed"}
                            style={{ padding: "4px 10px" }}>Complete</button>
                          <button onClick={() => transferAction(t, "delete")}
                            disabled={t.deleted}
                            style={{ padding: "4px 10px" }}>Delete</button>
                          {/* The record's own history - raised, who was emailed,
                              what was picked, what the receiving branch said.
                              It has always been written and never shown, which
                              is why a transfer that misbehaved could only be
                              investigated in the database. */}
                          <button onClick={() => setOpenLog((m) => ({ ...m, [rowKey]: !m[rowKey] }))}
                            title="Show everything that has happened to this transfer"
                            style={{ padding: "4px 10px",
                                     fontWeight: openLog[rowKey] ? 700 : 400 }}>
                            Log{(t.log || []).length ? ` (${t.log.length})` : ""}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {openLog[rowKey] && (
                      <tr key={`${rowKey}-log`}>
                        <td colSpan={9} style={{ ...td, background: "#f8fafc", padding: "10px 14px" }}>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>
                            History{t.reference ? ` - ${t.reference}` : ""}
                          </div>
                          {(t.log || []).length === 0 ? (
                            <div style={{ color: "#64748b" }}>
                              Nothing recorded. Transfers raised before the log was kept have no history.
                            </div>
                          ) : (
                            <ol style={{ margin: 0, paddingLeft: 18, fontSize: "0.95em", lineHeight: 1.6 }}>
                              {t.log.map((e, n) => (
                                <li key={n} style={{
                                  color: /NOT sent|shortfall|SHORTFALL|NOTHING received/i.test(e.action || "")
                                    ? "#b91c1c" : "#334155",
                                }}>
                                  <span style={{ color: "#94a3b8", marginRight: 8 }}>{fmtWhen(e.createdAt)}</span>
                                  {e.action}
                                  {/* Only worth showing where the row merges more
                                      than one request - otherwise it repeats the
                                      heading on every line. */}
                                  {e.reference && (t.sourceIds || []).length > 1 && (
                                    <span style={{ color: "#94a3b8" }}> · {e.reference}</span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            );
            })()}
          </div>
        </>
      )}

      {/* ---- email preview ---- */}
      {preview && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 1500 }}
            onClick={() => setPreview(null)} />
          <div style={{
            position: "fixed", top: "6vh", left: "50%", transform: "translateX(-50%)",
            width: 760, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto", zIndex: 1510,
            background: "#fff", border: "1px solid #cbd5e1", borderRadius: 10, padding: "14px 16px",
            boxShadow: "0 16px 40px rgba(15,23,42,0.4)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong>{preview.title}</strong>
              <button onClick={() => setPreview(null)} style={{ padding: "5px 12px" }}>Close</button>
            </div>
            {preview.html
              ? <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 12 }}
                  dangerouslySetInnerHTML={{ __html: preview.html }} />
              : <div style={{ color: "#64748b" }}>No preview was returned for this option.</div>}
          </div>
        </>
      )}

      {/* ---- guide overlay ---- */}
      {guideStep >= 0 && GUIDE[guideStep] && (
        <>
          {/* Clicking the backdrop pauses rather than quits - it is the
              natural gesture when you want a moment to look at what is being
              pointed at, and losing the guide for it would be punishing. */}
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 1300 }}
            onClick={() => setGuidePaused(true)} />
          <div style={{
            position: "fixed", left: "50%", bottom: 40, transform: "translateX(-50%)",
            width: 460, maxWidth: "90vw", zIndex: 1320, background: "#fff",
            border: "2px solid #2b7de9", borderRadius: 10, padding: "16px 18px",
            boxShadow: "0 12px 32px rgba(15,23,42,0.3)",
          }}>
            <div style={{ fontSize: "0.78em", color: "#64748b", marginBottom: 4 }}>
              Step {guideStep + 1} of {GUIDE.length}
              {guidePaused && <span style={{ color: "#BA7517", fontWeight: 600 }}> · paused</span>}
            </div>
            <div style={{ fontWeight: 700, fontSize: "1.05em", marginBottom: 6 }}>{GUIDE[guideStep].title}</div>
            <div style={{ fontSize: "0.9em", lineHeight: 1.55, color: "#334155" }}>{GUIDE[guideStep].body}</div>
            {/* Progress, so a paused guide does not look like a stuck one. */}
            <div style={{ height: 3, background: "#e5e7eb", borderRadius: 2, marginTop: 12 }}>
              <div style={{
                height: "100%", borderRadius: 2, background: "#2b7de9",
                width: `${((guideStep + 1) / GUIDE.length) * 100}%`,
                transition: "width 0.3s",
              }} />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
              <button onClick={() => { setGuideStep(-1); setOpenPanel(null); }}
                style={{ padding: "6px 12px" }}>Skip</button>

              <span style={{ flex: 1 }} />

              <button
                onClick={() => setGuideStep((s) => Math.max(0, s - 1))}
                disabled={guideStep === 0}
                style={{ padding: "6px 12px", opacity: guideStep === 0 ? 0.4 : 1 }}
              >
                Back
              </button>

              {/* Pausing is the point: people read at very different speeds,
                  and a panel that moves on mid-sentence is worse than none. */}
              <button
                onClick={() => setGuidePaused((p) => !p)}
                style={{ padding: "6px 12px", fontWeight: 600, minWidth: 78 }}
              >
                {guidePaused ? "▶ Resume" : "❚❚ Pause"}
              </button>

              <button onClick={() => setGuideStep((s) => s + 1)}
                style={{ padding: "6px 14px", background: "#2b7de9", color: "#fff", border: "none", borderRadius: 4, fontWeight: 600 }}>
                {guideStep === GUIDE.length - 1 ? "Done" : "Next"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}